import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    Select
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import classNames from 'classnames';
import { intervalToDuration } from 'date-fns';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { getUserProjectsAndItems } from '../api/auth';
import { getDataSchema } from '../api/data';
import { AccountContext } from '../context/account';
import { UpdateExploreDataProps } from '../context/explore';
import { ModelTypes, ProjectItem } from '../core/global';
import { calculateCellResolution } from '../helpers/core';
import styles from "../styles/components/export-modal.module.scss";
import { ErrorMessage, InfoMessage, WarningMessage } from './error-message';
import Loader from './loader';
import { ModalTitle } from './modal-title';
import HorizontalStepper from './stepper';

interface RenderRadioProps {
  label: string
  type: 'primary' | 'secondary' | 'none'
  id: string | number
}

const steps = ['Data sources', 'Setup', 'Export']

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ProjectsColumn = ({
  projects,
  id,
  title,
  value,
  onChange,
  showNone = true,
  required = false,
}: {
  projects: ProjectItem[]
  id: string
  title: string
  value: string
  onChange: (selectedValue: string, selectedItem: ProjectItem | null) => void
  showNone?: boolean
  required?: boolean
}) => {
  return (
    <div>
      <FormControl>
        <InputLabel id={id}>{title}</InputLabel>
        <Select
          label={title}
          size="small"
          // style={{ width: "180px", marginRight: "8px" }}
          labelId={id}
          id={id}
          value={value}
        >
          {showNone && 
            <MenuItem
              key={`${id}-none`}
              value={`none`}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                onChange('none', null)
              }}
            >
              - Do not import -
            </MenuItem>
          }
          {projects.map((item, index) => {

            const {distance, lat, lon, model, provider, version, geography, id: projectId} = item as ProjectItem
            let showDistance = ''
            if (distance) {
              showDistance = ` (${numeral(distance/1000).format("0,0.0")} km)`
            }

            return (
              <MenuItem
                key={`${index}-${projectId}`}
                value={`${projectId}` as string}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  onChange(`${projectId}` as string, item)
                }}
              >
                {`${lat}, ${lon} - ${model}${ version !== null ? ` ver. ${version}` : ``}, ${provider}, ${geography}`}{showDistance}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  )
}

interface Option {
  key: string
  title: string
}

const TemporalFrequencySelector = ({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (selectedValue: string, selectedItem: Option) => void
}) => {
  const options: Option[] = [
    // {key: 'auto', title: 'Automatic (use base data frequency)'},
    // {key: '20', title: '20 minutes'},
    {key: '60', title: '60 minutes'},
  ]

  const title = `Synchronise time stamp`
  return (
    <div>
      <FormControl required sx={{width: '100%'}}>
        <InputLabel id={id}>{title}</InputLabel>
        <Select
          label={title}
          size="small"
          // style={{ width: "180px", marginRight: "8px" }}
          labelId={id}
          id={id}
          value={value}
          sx={{width: '100%'}}
        >
          {options.map((item, index) => {

            const {title, key} = item

            return (
              <MenuItem
                key={`${index}-${key}`}
                value={`${key}`}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  onChange(`${key}` as string, item)
                }}
              >
                {title}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  )
}

interface EditProjectModalProps {
  onClose: () => void
}

export const ExportModal = ({
  onClose,
}: EditProjectModalProps ) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [exportError, setExportError] = React.useState<string[]>([])
  const [columnOneValue, setColumnOneValue] = React.useState<string>('none')
  const [columnTwoValue, setColumnTwoValue] = React.useState<string>('none')
  const [warning, setWarning] = React.useState<string | null>(null)
  const [dataOneDescription, setDataOneDescription] = React.useState<any>(null)
  const [loadingOne, setLoadingOne] = React.useState<boolean>(false)
  const [dataTwoDescription, setDataTwoDescription] = React.useState<any>(null)
  const [loadingTwo, setLoadingTwo] = React.useState<boolean>(false)
  const [secondaryProjects, setSecondaryProjects] = React.useState<ProjectItem[]>([])
  const [frequencyKey, setFrequencyKey] = React.useState<string>('60')
  const [selectedVectors, setSelectedVectors] = React.useState<{[key: string]: 'primary' | 'secondary' | 'none'}>({})

  const {
    projects,
  } = useContext(AccountContext);

  useEffect(() => {
    setExportError([])
  }, [activeStep])

  useEffect(() => {
    if (columnOneValue !== 'none' && columnOneValue === columnTwoValue) {
      setWarning('Both selected project data points are the same.')
    } else {
      setWarning(null)
    }
  }, [columnOneValue, columnTwoValue])

  useEffect(() => {
    if (projects && Array.isArray(projects) && projects.length > 0) {
      const [selectedItem] = projects
      setColumnOneValue(`${selectedItem.id}`)
      const {
        model, 
        provider,
        geography, 
        version,
        lat, 
        lon
      } = selectedItem
      updateDataOneDescription({
        model, 
        provider, 
        geography,
        version,
        lat, 
        lon
      })
    }
  }, [projects])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };


  // const mergedPoints: (LatLngExpression | ProjectItem)[] = useMemo(() => {
  //   if (showProjectPointsOnMap) {
  //     return [...projectPoints, ...projects]
  //   }
  //   return []
  // }, [projects, projectPoints])


  const updateDataOneDescription = useCallback(({
    model, 
    provider, 
    geography,
    version,
    lat, 
    lon
  }: any) => {
    setLoadingOne(true)

    const fetchSecondaryProjects = async (filename: undefined | string | null) => {
      const {response} = await getUserProjectsAndItems({lat, lon});
      if (response) {
        const {projects} = response
        if (projects && Array.isArray(projects)) {
          setSecondaryProjects(projects.filter((p) => {
            const {path} = p
            const [, , secondaryFilename] = (path || '').split('/')
            return filename && secondaryFilename !== filename
          }))
          setDataTwoDescription(null)
          setColumnTwoValue('none')
          return
        }
      }
      setSecondaryProjects([])
    }

    const execute = async () => {
      const schemaParams: UpdateExploreDataProps = {
        source: "azure",
        model,
        provider,
        geography,
        version,
        lat,
        lon,
        lengthYears: 1,
        preview: true,
        cache: false,
      }

      const result = await getDataSchema({
        ...schemaParams
      });
      const { response, error, message } = result;

      const { description, timeline, vectors } = response || {};
      
      fetchSecondaryProjects(description?.filename)

      setDataOneDescription({ description, timeline, vectors })
      setLoadingOne(false)
    }
    execute()
  }, [])

  const updateDataTwoDescription = useCallback(({
    model, 
    provider, 
    geography,
    version,
    lat, 
    lon
  }: any) => {
    setLoadingTwo(true)
    const execute = async () => {
      const schemaParams: UpdateExploreDataProps = {
        source: "azure",
        model,
        provider,
        geography,
        version,
        lat,
        lon,
        lengthYears: 1,
        preview: true,
        cache: false,
      }

      const result = await getDataSchema({
        ...schemaParams
      });
      const { response, error, message } = result;

      const { description, timeline, vectors } = response || {};
      setDataTwoDescription({ description, timeline, vectors })
      setLoadingTwo(false)
    }
    execute()
  }, [dataTwoDescription])

  const getFrequency = useCallback((dataObject: any) : any  => {
      const { description, timeline } = dataObject || {}
      const sqm = description?.sqm || null
      const model = description?.model || null
      let useSqm = null
      if (sqm !== null && model !== null) {
        useSqm = calculateCellResolution(sqm, model as ModelTypes)
      }
      const frequency = timeline?.frequency || null
      let useFrequency = ''
      if (frequency) {
        const formatted = intervalToDuration({start: 0, end: frequency*1000})
        useFrequency = formatted.hours && formatted.hours > 0 ? `${formatted.hours} hour` : `${formatted.minutes} minutes`
      }
      return {
        frequency: useFrequency,
        sqm: useSqm,
      }
  }, [])



const RenderRadio = useCallback(({label, type, id}: RenderRadioProps) => {
  const isSelected = selectedVectors[id] === type
  if (label === 'N/A') {
    return <span>N/A</span>
  }

  return (
    <FormControlLabel
      key={id}
      className={styles.formControl}
      control={
        <Radio
          name={id  as string}
          value={id as string}
          checked={isSelected}
          onChange={(e: React.SyntheticEvent) => {
            setSelectedVectors((vectors: any) => {
              return {
                ...vectors,
                [id]: type
              }
            })
          }}
          color="primary" 
        />}
      label={`${label}`}
    />
  )
}, [selectedVectors])

const columns: any = useMemo( () => {
  if (columnTwoValue === 'none') {
    return [
      {
        field: 'primary_data_source',
        headerName: 'Primary data source',
        width: 600,
        editable: false,
        // renderHeader: (params: GridColumnHeaderParams) => {
        //   return (
        //     <RenderCheckBox
        //       label={'Primary data source'}
        //       type="primary"
        //     />
        //   )
        // },
        renderCell: (params: GridRenderCellParams<any, Date>) => {
          return (<RenderRadio 
            label={`${params?.value}`}
            type="primary"
            id={params.id}
          />)
        },
      },
      {
        field: 'omit',
        headerName: 'Omit',
        width: 150,
        editable: false,
        renderCell: (params: GridRenderCellParams<any, Date>) => {
          return (<RenderCheckBox 
            label={``}
            type="none"
            id={params.id}
          />)
        },
      }
    ]
  }
  return [
    {
      field: 'primary_data_source',
      headerName: 'Primary data source',
      width: 300,
      editable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        return (<RenderRadio 
          label={`${params?.value}`}
          type="primary"
          id={params.id}
        />)
      },

    },
    {
      field: 'secondary_data_source',
      headerName: 'Secondary data source',
      width: 300,
      editable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        return (<RenderRadio 
          label={`${params?.value}`}
          type="secondary"
          id={params.id}
        />)
      },

    },
    {
      field: 'omit',
      headerName: 'Omit',
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        return (<RenderCheckBox 
          label={``}
          type="none"
          id={params.id}
        />)
      },

    }
    // {
    //   field: 'time_to_execute',
    //   headerName: 'Execution (ms)',
    //   type: 'number',
    //   width: 120,
    //   editable: false,
    //   valueGetter: (value: number, row: number) => `${value ? numeral(value).format("0,0.") : ''}`,
    // },
    // {
    //   field: 'message',
    //   headerName: 'Message',
    //   type: 'string',
    //   width: 280,
    //   editable: false,
    //   valueGetter: (value: number, row: number) => `${value || ''}`,
    // },
    ];
  }, [columnTwoValue, selectedVectors])

  const vectors: any[] = useMemo( () => {
    const {vectors: dataOneVectors} = dataOneDescription || {}
    const {vectors: dataTwoVectors} = dataTwoDescription || {}

    const dataOneVectorTitles = dataOneVectors ? dataOneVectors.map((v: any) => v.name) : []
    const dataTwoVectorTitles = dataTwoVectors ? dataTwoVectors.map((v: any) => v.name) : []
    const result: any[] = []
    const selected: any = {}

    dataOneVectorTitles.forEach((title: string) => {
      const id = nanoid()
      selected[id] = 'primary'
      result.push({
        id,
        primary_data_source: title,
        secondary_data_source: dataTwoVectorTitles.includes(title) ? title : 'N/A',
        action: 'None',
      })
    })
    dataTwoVectorTitles.forEach((title: string) => {
      if (!dataOneVectorTitles.includes(title)) {
        const id = nanoid()
        selected[id] = 'secondary'
        result.push({
          id,
          primary_data_source: 'N/A',
          secondary_data_source: title,
          action: 'None',
        })
      }
      
    })
    setSelectedVectors(selected)
    return result
  }, [dataOneDescription, dataTwoDescription])

  const handleCheckboxToggle = useCallback((id: number | string, type: 'primary' | 'secondary' | 'none', isSelected: boolean) => {
    let useType = type

    if (isSelected) {
      const find = vectors.find((v) => v.id === id)
      if (find) {
        const{
          primary_data_source,
          secondary_data_source,
          } = find
        if (primary_data_source !== 'N/A') {
          useType = 'primary'
        } else if (secondary_data_source !== 'N/A') {
          useType = 'secondary'
        }
      }
    }

    setSelectedVectors((vectors: any) => {
      return {
        ...vectors,
        [id]: useType
      }
    })

  }, [vectors])

  const RenderCheckBox = useCallback(({label, type, id}: RenderRadioProps) => {
    const isSelected = selectedVectors[id] === type
    return (
      <FormControlLabel
        className={styles.formControl}
        control={
          <Checkbox
            name={id  as string}
            value={id as string}
            checked={isSelected}
            onChange={(e: React.SyntheticEvent) => {
              handleCheckboxToggle(id, type, isSelected)
            }}
            color="primary" 
          />}
        label={label}
      />
    )
  }, [selectedVectors, vectors])
  
  return (
    <Dialog
      id="export-data-modal"
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Merge data</ModalTitle>
      <div className={styles.dialogContent}>
        <ErrorMessage message={exportError} />
        <HorizontalStepper
          activeStep={activeStep}
          steps={steps}
        >
          <>
            <p>Select the projects that you would like to combine and export.</p>
            <div className={classNames(styles.twoColumns)}>
              <div>
                <ProjectsColumn
                  id="export-data-source-one"
                  projects={projects} 
                  title={'Primary data source'}
                  value={columnOneValue} 
                  onChange={(selectedValue: string, selectedItem: ProjectItem | null) => {
                    setColumnOneValue(selectedValue)
                    if (selectedItem) {
                      const {
                        model, 
                        provider, 
                        geography,
                        version,
                        lat, 
                        lon
                      } = selectedItem
                      updateDataOneDescription({
                        model, 
                        provider, 
                        geography,
                        version,
                        lat, 
                        lon
                      })
                    }
                    
                  }} 
                  showNone={false}
                  required={true}
                />
                <div className={styles.columnPlus}>
                  {loadingOne ?
                    <Loader small center className={styles.loader} />
                    : 
                    <div className={styles.columnContent}>
                      {(dataOneDescription?.vectors && dataOneDescription?.vectors.length > 0) ?
                        <>
                          <strong>Approximate cell resolution:</strong> {numeral(getFrequency(dataOneDescription)?.sqm).format("0,0.0")} m<br />
                          <strong>Parameters:</strong> {dataOneDescription?.vectors.map((v: any) => v.name).join(', ')}<br />
                          <strong>Temporal frequency:</strong> {getFrequency(dataOneDescription)?.frequency}
                        </>
                        :
                        null
                      }
                    </div>
                  }
                </div>
              </div>
              <div>
                <ProjectsColumn 
                  id="export-data-source-two"
                  projects={secondaryProjects} 
                  title={'Secondary data source (optional)'}
                  value={columnTwoValue} 
                  onChange={(selectedValue: string, selectedItem: ProjectItem | null) => {
                    setColumnTwoValue(selectedValue)
                    if (selectedItem) {
                      const {
                        model, 
                        provider, 
                        geography, 
                        version,
                        lat, 
                        lon
                      } = selectedItem

                      updateDataTwoDescription({
                        model, 
                        provider, 
                        geography,
                        version, 
                        lat, 
                        lon
                      })
                    } else {
                      setDataTwoDescription(null)
                    }
                  }} 
                  showNone={true}
                />
                <div>
                {loadingTwo ?
                    <Loader small center className={styles.loader} />
                    : 
                    <div className={styles.columnContent}>
                      {(dataTwoDescription?.vectors && dataTwoDescription?.vectors.length > 0) ?
                        <>
                          <strong>Approximate cell resolution:</strong> {numeral(getFrequency(dataTwoDescription)?.sqm).format("0,0.0")} m<br />
                          <strong>Parameters:</strong> {dataTwoDescription?.vectors.map((v: any) => v.name).join(', ')}<br />
                          <strong>Temporal frequency:</strong> {getFrequency(dataTwoDescription)?.frequency}
                        </>
                        :
                        <InfoMessage message={`None selected.`} />
                      }
                      
                    </div>
                  }
                </div>
              </div>
            </div>
            {warning && <WarningMessage message={warning} />}
          </>

          <>
            <p>Please select the vectors that you would like to export for each data set.</p>


            <DataGrid
              className={styles.dataGrid}
              style={{ height: 400, width: '100%' }}
              rows={vectors}
              columns={columns}
              rowSelection={false}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[10]}
              // checkboxSelection
              // disableRowSelectionOnClick
              getRowClassName={(params) => {
                const doNotImport = styles.doNotImport;
                const {id} = params
                const isSelected = selectedVectors[id] === 'none'
                return isSelected ? doNotImport : '';
              }}
            />
            

            <div className={styles.fullWidth}>
              {(dataTwoDescription && dataTwoDescription?.vectors && dataTwoDescription?.vectors.length > 0) &&  
                <>
                  {getFrequency(dataOneDescription)?.frequency === getFrequency(dataTwoDescription)?.frequency ?
                    // <InfoMessage message={`Both data sources have the same temporal frequency of ${getFrequency(dataOneDescription)?.frequency}.` } />
                    null
                    : 
                    <WarningMessage message={`The two data sources have different temporal frequencies (${getFrequency(dataOneDescription)?.frequency}/${getFrequency(dataTwoDescription)?.frequency}). You will **have to** synchronise the timestamp.`} />
                  }
                </>
              }
              <TemporalFrequencySelector
                id="temporal-frequency-selector"
                value={frequencyKey}
                onChange={(selectedValue: string, selectedItem: Option) => {
                  setFrequencyKey(selectedValue)
                }}
              />
            </div>
          </>
          <div>
            <InfoMessage message={`We are currently **developing** this feature and will be releasing it very soon. `} />
          </div>
        </HorizontalStepper>
      </div>
      <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
        <>
          {activeStep === steps.length ? 
            <Button onClick={onClose}>Close</Button>
          : 
            <>
              <Button
                color="inherit"
                onClick={activeStep === 0 ? onClose : handleBack}
                sx={{ mr: 1 }}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              {activeStep !== steps.length - 1 && 
                <Button
                  id="cart-modal-next-button"
                  onClick={handleNext}
                >
                  Next
                </Button>
              }
            </>
          }
        </>

      </DialogActions>
    </Dialog>
  )
}
